import { useEffect, useRef } from 'react';

type RegisterSWFn = (options?: {
  immediate?: boolean;
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
  onRegisteredSW?: (
    url: string,
    reg: ServiceWorkerRegistration | undefined
  ) => void;
  onRegisterError?: (err: unknown) => void;
}) => (reloadPage?: boolean) => Promise<void>;

interface Options {
  registrationRef: React.MutableRefObject<ServiceWorkerRegistration | null>;
  onOfflineReady: () => void;
  onUpdateAvailable: () => void;
}

export function useServiceWorkerRegistration(
  registerSW: RegisterSWFn,
  { registrationRef, onOfflineReady, onUpdateAvailable }: Options
): void {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    registerSW({
      immediate: true,

      onRegisteredSW(_, reg) {
        if (!reg) return;
        registrationRef.current = reg;

        if (reg.waiting) {
          onUpdateAvailable(); // already has a waiting SW
        }

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          newWorker?.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              onUpdateAvailable();
            }
          });
        });
      },

      onOfflineReady() {
        console.log('[PWA] Offline ready');
        onOfflineReady();
      },

      onNeedRefresh() {
        console.log('[PWA] Update available via onNeedRefresh');
        onUpdateAvailable();
      },

      onRegisterError(err) {
        console.error('[PWA] SW registration failed:', err);
      },
    });
    // Callbacks excluded intentionally — they're stable refs from useCallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
